import React, { useState } from 'react';
import { Notification } from '../../types';

export interface NotificationBellProps {
  notifications: Notification[];
  onDismiss?: (id: string) => void;
  onClear?: () => void;
}

/**
 * Sino de notificações com dropdown de histórico recente.
 * Itens só saem ao clicar na lixeira individual ou em Limpar.
 * @param props - Lista de notificações e callbacks
 */
export const NotificationBell: React.FC<NotificationBellProps> = ({
  notifications,
  onDismiss,
  onClear,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const unreadCount = notifications.length;

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  const handleDismiss = (event: React.MouseEvent, id: string) => {
    event.stopPropagation();
    onDismiss?.(id);
  };

  const handleClear = () => {
    onClear?.();
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={handleToggle}
        className={`flex size-7 items-center justify-center rounded-full bg-white hover:bg-slate-50 transition-colors text-slate-500 relative border border-slate-200 ${unreadCount > 0 ? 'animate-pulse-subtle' : ''}`}
        aria-label={`Notificações${unreadCount > 0 ? `, ${unreadCount} pendentes` : ''}`}
        aria-expanded={isOpen}
      >
        <span className="material-symbols-outlined !text-[16px]">notifications</span>
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 size-3 bg-status-red text-white text-[8px] font-black rounded-full ring-2 ring-white flex items-center justify-center animate-in zoom-in">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} aria-hidden="true" />
          <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.15)] border border-slate-100 flex flex-col z-50 animate-in fade-in slide-in-from-top-2 duration-300 origin-top-right overflow-hidden">
            <div className="p-3 border-b border-slate-50 flex justify-between items-center bg-slate-50/30">
              <h4 className="text-xs font-bold text-slate-800 tracking-tight">Notificações</h4>
              {notifications.length > 0 && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="text-[9px] font-bold text-slate-400 hover:text-red-500 uppercase tracking-widest transition-colors"
                >
                  Limpar
                </button>
              )}
            </div>
            <div className="max-h-[280px] overflow-y-auto custom-scrollbar">
              {notifications.length > 0 ? (
                <div className="divide-y divide-slate-50">
                  {notifications.map((notif) => (
                    <div
                      key={notif.id}
                      className="p-3 hover:bg-slate-50/50 transition-colors flex gap-2 group/item"
                    >
                      <div
                        className={`size-6 rounded-full shrink-0 flex items-center justify-center ${
                          notif.type === 'add'
                            ? 'bg-green-100 text-green-600'
                            : notif.type === 'delete'
                              ? 'bg-red-100 text-red-600'
                              : 'bg-blue-100 text-blue-600'
                        }`}
                      >
                        <span className="material-symbols-outlined !text-[14px]">
                          {notif.type === 'add' ? 'person_add' : notif.type === 'delete' ? 'person_remove' : 'info'}
                        </span>
                      </div>
                      <div className="flex flex-col gap-0.5 overflow-hidden flex-1 min-w-0">
                        <p className="text-[11px] font-bold text-slate-800 truncate">{notif.title}</p>
                        <p className="text-[10px] text-slate-500 leading-relaxed break-words">{notif.message}</p>
                        <span className="text-[8px] text-slate-300 font-bold mt-0.5">
                          {new Date(notif.timestamp).toLocaleTimeString('pt-BR', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={(event) => handleDismiss(event, notif.id)}
                        className="size-6 shrink-0 flex items-center justify-center rounded-md text-slate-300 hover:text-red-500 hover:bg-red-50 transition-all"
                        aria-label="Remover notificação"
                      >
                        <span className="material-symbols-outlined !text-[14px]">delete</span>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 flex flex-col items-center justify-center gap-2 text-slate-300">
                  <span className="material-symbols-outlined !text-[24px] opacity-20">notifications_off</span>
                  <p className="text-[10px] font-bold uppercase tracking-widest">Sem avisos</p>
                </div>
              )}
            </div>
            {notifications.length > 0 && (
              <div className="p-2 bg-slate-50/30 border-t border-slate-50 text-center">
                <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">Histórico Recente</span>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
